import { useEffect, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useClient } from 'urql';
import Avatar from '@/components/Custom/Avatar';
import Button from '@/components/UI/Button';
import PlatformSelect from '@/components/UI/PlatformSelect';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import {
  GetPersonContextOptions,
  PERSON_CONTEXT_PAGE_SIZE,
} from './PersonContextQueries';

type ScopeOption = {
  label: string;
  value: string;
  avatar?: { id: string | null; color: string | null };
};

export default function PersonScopeSelect({
  personId,
  value,
  label,
  onValueChange,
}: {
  personId: string;
  value: string;
  label: string;
  onValueChange: (value: string) => void;
}) {
  const client = useClient();
  const [options, setOptions] = useState<ScopeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      const choices: ScopeOption[] = [];
      for (let offset = 0; active; offset += PERSON_CONTEXT_PAGE_SIZE) {
        const result = await client
          .query(
            GetPersonContextOptions,
            {
              personId,
              offset,
              limit: PERSON_CONTEXT_PAGE_SIZE,
            },
            { requestPolicy: attempt ? 'network-only' : 'cache-first' },
          )
          .toPromise();
        if (!active) return;
        if (result.error || !result.data) {
          setFailed(true);
          setLoading(false);
          return;
        }
        choices.push(
          ...result.data.eventContexts.flatMap(({ event }) =>
            event
              ? [
                  {
                    label: event.title ?? 'Untitled organization',
                    value: `organization:${event.id}`,
                  },
                ]
              : [],
          ),
          ...result.data.teamMemberships.map(({ team }) => ({
            label: team.title ?? 'Untitled team',
            value: `team:${team.id}`,
            avatar: { id: team.avatar_id, color: team.home_primary_color },
          })),
        );
        if (
          result.data.teamMemberships.length < PERSON_CONTEXT_PAGE_SIZE &&
          result.data.eventContexts.length < PERSON_CONTEXT_PAGE_SIZE
        )
          break;
      }
      if (active) setOptions(choices);
      if (active) setLoading(false);
    };
    void load().catch(() => {
      if (!active) return;
      setFailed(true);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [client, personId, attempt]);

  const selectedOption = options.find((option) => option.value === value);
  const displayedOptions = loading && value ? [{ label, value }] : options;

  return (
    <Box style={styles.fill}>
      <PlatformSelect
        style={styles.fill}
        leadingAccessory={
          value.startsWith('team:') ? (
            <Avatar
              id={selectedOption?.avatar?.id ?? undefined}
              color={selectedOption?.avatar?.color}
              name={selectedOption?.label ?? ''}
              size="sm"
            />
          ) : value.startsWith('organization:') ? (
            <Box width={32} height={32} center>
              <Icon
                name="globe-outline"
                size="2xl"
                variant="typographySecondary"
              />
            </Box>
          ) : undefined
        }
        testID="PersonDetailScope"
        accessibilityLabel="Person context"
        placeholder={loading ? 'Loading contexts…' : 'Choose context'}
        disabled={loading || failed || options.length === 0}
        value={value}
        options={displayedOptions}
        onValueChange={onValueChange}
      />
      {failed && (
        <Box direction="horizontal" align="center" space="sm">
          <Text variant="error">Could not load contexts.</Text>
          <Button variant="secondary" onPress={() => setAttempt(attempt + 1)}>
            Retry
          </Button>
        </Box>
      )}
    </Box>
  );
}

const styles = StyleSheet.create({
  fill: { flexGrow: 1 },
});
